<template>
  <div class="series-upload-session" v-if="visible">
    <div class="upload-modal-overlay" @click="handleOverlayClick">
      <div class="upload-modal" @click.stop>
        <div class="modal-header">
          <h3>Uploading Files</h3>
          <button class="close-btn" @click="handleClose" :disabled="isActivelyUploading">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="modal-body">
          <!-- Overall Progress -->
          <div class="overall-progress">
            <div class="progress-info">
              <span class="status-text">{{ overallStatusText }}</span>
              <span class="percent-text">{{ overallPercent }}%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: overallPercent + '%' }"></div>
            </div>
          </div>

          <!-- File List -->
          <div class="file-list">
            <div
              v-for="file in orderedFileStates"
              :key="file.blob_path"
              class="file-item"
              :class="'status-' + file.status"
            >
              <div class="file-icon">
                <i
                  class="bi"
                  :class="{
                    'bi-file-earmark': file.status === 'pending',
                    'bi-arrow-repeat spinner': file.status === 'uploading' || file.status === 'uploaded',
                    'bi-check-circle-fill': file.status === 'committed',
                    'bi-exclamation-circle-fill': file.status === 'failed'
                  }"
                ></i>
              </div>

              <div class="file-info">
                <div class="file-name">{{ file.filename }}</div>
                <div class="file-experiment">{{ file.experimentName }}</div>
                <div class="file-status-text">{{ getStatusText(file) }}</div>
                <div v-if="file.status === 'uploading'" class="file-progress-bar">
                  <div class="file-progress-fill" :style="{ width: file.progress + '%' }"></div>
                </div>
                <div v-if="file.error" class="file-error">{{ file.error }}</div>
              </div>

              <div class="file-actions">
                <button
                  v-if="file.status === 'failed'"
                  class="retry-btn"
                  @click="retryFile(file)"
                  :disabled="file.retrying"
                >
                  <i class="bi bi-arrow-clockwise"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="btn-secondary"
            @click="handleFinalize"
            :disabled="isActivelyUploading"
          >
            {{ finalizeButtonText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiService from "@/services/apiService";
import { useToast } from "vue-toastification";

/**
 * SeriesUploadSession
 *
 * Handles post-creation file uploads for a batch of experiments created via CreateSeries.
 * Unlike FileUploadSession (which initializes its own upload session for a single entity),
 * this component receives pre-resolved upload info from the createSeries API response and
 * uses the completeFiles endpoint for committing (no session_id required).
 *
 * All files for all experiments are uploaded concurrently in a single modal,
 * each row labelled with its experiment name.
 */
export default {
  name: "SeriesUploadSession",

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * Array of per-experiment upload descriptors, straight from the createSeries
     * API response merged with local File objects:
     *
     * [{
     *   experimentId: Number,
     *   experimentName: String,
     *   uploads: [{ blob_path, upload_url, filename, relative_path, original_relative_path }],
     *   localFiles: File[]   // original File objects from the UI
     * }]
     */
    uploadData: {
      type: Array,
      required: true,
    },
    entraId: {
      type: String,
      required: true,
    },
  },

  emits: ["complete", "close"],

  setup() {
    const toast = useToast();
    return { toast };
  },

  data() {
    return {
      // Flat list of all file upload states across all experiments
      fileStates: [],
      overallPercent: 0,
    };
  },

  computed: {
    orderedFileStates() {
      // Show failed files at the top so they're immediately visible
      const failed = this.fileStates.filter((f) => f.status === "failed");
      const rest = this.fileStates.filter((f) => f.status !== "failed");
      return [...failed, ...rest];
    },

    isActivelyUploading() {
      return this.fileStates.some(
        (f) => f.status === "uploading" || f.status === "uploaded"
      );
    },

    overallStatusText() {
      const total = this.fileStates.length;
      if (total === 0) return "Preparing uploads...";

      const committed = this.fileStates.filter((f) => f.status === "committed").length;
      const failed = this.fileStates.filter((f) => f.status === "failed").length;

      if (committed === total) return "All files uploaded successfully";
      if (failed > 0) return `${committed} of ${total} files uploaded (${failed} failed)`;
      return `Uploading ${committed} of ${total} files...`;
    },

    finalizeButtonText() {
      if (this.isActivelyUploading) return "Uploading...";
      const failed = this.fileStates.filter((f) => f.status === "failed").length;
      return failed > 0 ? "Finish (Skip Failed Files)" : "Done";
    },
  },

  watch: {
    visible(newVal) {
      if (newVal && Array.isArray(this.uploadData) && this.uploadData.length > 0) {
        this.startUpload();
      }
    },
  },

  methods: {
    async startUpload() {
      // Build a flat fileStates list from all experiments
      const fileRelPath = (f) =>
        f.webkitRelativePath && f.webkitRelativePath.length > 0
          ? f.webkitRelativePath
          : f.name;

      this.fileStates = [];

      for (const expData of this.uploadData) {
        const { experimentId, experimentName, uploads = [], localFiles = [] } = expData;

        // Build a lookup from original_relative_path / filename → File object
        const fileMap = new Map(localFiles.map((f) => [fileRelPath(f), f]));

        for (const info of uploads) {
          const fileObject =
            fileMap.get(info.original_relative_path) ||
            fileMap.get(info.relative_path) ||
            fileMap.get(info.filename);

          this.fileStates.push({
            blob_path: info.blob_path,
            filename: info.filename || info.relative_path,
            experimentId,
            experimentName,
            status: "pending",
            progress: 0,
            error: null,
            uploadInfo: info,
            fileObject,
            retrying: false,
          });
        }
      }

      if (this.fileStates.length === 0) {
        // Nothing to upload — immediately signal complete
        this.$emit("complete");
        return;
      }

      // Upload all files with bounded concurrency (consistent with FileUploadSession)
      const maxConcurrent = 8;
      let nextIndex = 0;

      const worker = async () => {
        while (nextIndex < this.fileStates.length) {
          const idx = nextIndex++;
          await this.uploadAndCommitFile(this.fileStates[idx]);
        }
      };

      const workerCount = Math.min(maxConcurrent, this.fileStates.length);
      await Promise.allSettled(Array.from({ length: workerCount }, () => worker()));

      const allSuccess = this.fileStates.every((f) => f.status === "committed");
      if (allSuccess) {
        this.$emit("complete");
        this.toast.success("All files uploaded successfully!");
      }
    },

    async uploadAndCommitFile(fileState) {
      try {
        if (!fileState.fileObject) {
          throw new Error(`Could not match local file for "${fileState.filename}"`);
        }
        if (!fileState.uploadInfo?.upload_url) {
          throw new Error(`Missing upload URL for "${fileState.filename}"`);
        }

        fileState.status = "uploading";
        fileState.progress = 0;
        fileState.error = null;

        // Upload to Azure Blob Storage with real-time progress
        await apiService.uploadFileToBlob(
          fileState.uploadInfo.upload_url,
          fileState.fileObject,
          (loaded, total) => {
            fileState.progress = total > 0 ? Math.min(100, Math.floor((loaded / total) * 100)) : 0;
            this.updateOverallProgress();
          }
        );

        fileState.status = "uploaded";
        fileState.progress = 100;

        // Commit to the database (no session_id for this flow)
        await apiService.completeFiles(
          "experiment",
          fileState.experimentId,
          this.entraId,
          [
            {
              blob_path: fileState.blob_path,
              relative_path: fileState.uploadInfo.relative_path,
              original_relative_path:
                fileState.uploadInfo.original_relative_path ||
                fileState.uploadInfo.relative_path,
            },
          ],
          null
        );

        fileState.status = "committed";
        this.updateOverallProgress();
      } catch (error) {
        fileState.status = "failed";
        fileState.error = error.message || String(error);
        console.error(`Failed to upload "${fileState.filename}":`, error);
      }
    },

    async retryFile(fileState) {
      if (fileState.retrying) return;
      fileState.retrying = true;
      fileState.error = null;

      try {
        await this.uploadAndCommitFile(fileState);
        if (fileState.status === "committed") {
          this.toast.success(`${fileState.filename} uploaded successfully!`);
        }
      } catch {
        this.toast.error(`Retry failed for ${fileState.filename}`);
      } finally {
        fileState.retrying = false;
      }
    },

    updateOverallProgress() {
      const totalSize = this.fileStates.reduce(
        (sum, f) => sum + (f.fileObject?.size || 0),
        0
      );
      if (totalSize === 0) {
        this.overallPercent = 0;
        return;
      }
      const uploadedSize = this.fileStates.reduce((sum, f) => {
        const sz = f.fileObject?.size || 0;
        if (f.status === "committed") return sum + sz;
        if (f.status === "uploading") return sum + (sz * f.progress) / 100;
        return sum;
      }, 0);
      this.overallPercent = Math.min(100, Math.floor((uploadedSize / totalSize) * 100));
    },

    handleOverlayClick() {
      if (!this.isActivelyUploading) this.handleClose();
    },

    handleClose() {
      if (!this.isActivelyUploading) this.$emit("close");
    },

    handleFinalize() {
      this.$emit("complete");
    },

    getStatusText(file) {
      switch (file.status) {
        case "pending":   return "Waiting...";
        case "uploading": return `Uploading... ${file.progress}%`;
        case "uploaded":  return "Committing...";
        case "committed": return "Complete ✓";
        case "failed":    return "Failed";
        default:          return "";
      }
    },
  },
};
</script>

<style scoped>
.upload-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.upload-modal {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 620px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  padding: 4px 8px;
  transition: color 0.2s;
}

.close-btn:hover:not(:disabled) { color: #333; }
.close-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.overall-progress {
  margin-bottom: 24px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
}

.status-text {
  color: #333;
  font-weight: 500;
}

.percent-text {
  color: #666;
  font-weight: 600;
}

.progress-bar {
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #66bb6a);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.file-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  transition: border-color 0.2s;
}

.file-item.status-committed { background: #f1f8f4; border-color: #4caf50; }
.file-item.status-failed    { background: #fff5f5; border-color: #f44336; }
.file-item.status-uploading { border-color: #2196f3; }

.file-icon {
  font-size: 24px;
  width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-icon .bi-file-earmark           { color: #999; }
.file-icon .bi-arrow-repeat.spinner   { color: #2196f3; animation: spin 1s linear infinite; }
.file-icon .bi-check-circle-fill      { color: #4caf50; }
.file-icon .bi-exclamation-circle-fill { color: #f44336; }

@keyframes spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 2px;
}

.file-experiment {
  font-size: 12px;
  color: #888;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-status-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
}

.file-progress-bar {
  height: 4px;
  background: #e0e0e0;
  border-radius: 2px;
  overflow: hidden;
  margin-top: 6px;
}

.file-progress-fill {
  height: 100%;
  background: #2196f3;
  transition: width 0.3s ease;
  border-radius: 2px;
}

.file-error {
  font-size: 12px;
  color: #f44336;
  margin-top: 4px;
}

.file-actions {
  display: flex;
  align-items: center;
}

.retry-btn {
  background: none;
  border: 1px solid #2196f3;
  color: #2196f3;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
}

.retry-btn:hover:not(:disabled) { background: #2196f3; color: white; }
.retry-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

.btn-secondary {
  padding: 10px 20px;
  background: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s;
}

.btn-secondary:hover:not(:disabled) { background: #1976d2; }
.btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
